import { getDecisionUrl, getUrl } from '@shared/lib/utils'
import type { ParserLink, LawRef } from './parser'
import type { IDecision } from '@shared/lib/norms.types'

const PRODUCTION_DOMAIN = 'https://gesetzefinden.at'

/** Resolved paragraph refs keep `documentType: 'paragraph'` but carry law fields from Typesense (same as clause/law). */
export function hasResolvableLawFields(ref: ParserLink): ref is LawRef {
  const r = ref as Partial<LawRef>
  return Boolean(
    r.lawType && r.normId && r.enumeration && (r.abbreviation || r.normId),
  )
}

/**
 * In-app path for a resolved ref — THE ref→URL rule, shared by `RefButton`
 * chips and the PDF annotation builder. When `clauseSlug` is given (the
 * clause's real PG slug, see pdfLinker's `applyClauseSlugUrls`) it replaces
 * the `urlify(enumeration)` path segment. Without a slug, clause deep-links
 * are only built for refs WITH an abbreviation: EU acts (no abbreviation)
 * store clause slugs ("artikel-2") that differ from `urlify(enumeration)`
 * ("art-2") and would 404 — those link to the law page instead, until
 * Typesense carries the real slugs.
 */
export function getRefPath(ref: ParserLink, clauseSlug?: string): string {
  const docType = (ref as { documentType?: string }).documentType

  if (docType === 'decision' || docType === 'rechtssatz') {
    return getDecisionUrl(ref as unknown as IDecision)
  }
  if (
    docType === 'law' ||
    docType === 'clause' ||
    (docType === 'paragraph' && hasResolvableLawFields(ref))
  ) {
    const { lawType, abbreviation, enumeration, normId } =
      ref as unknown as LawRef
    return getUrl(lawType, {
      norm: abbreviation || normId,
      normId,
      ...(clauseSlug
        ? { clauseSlug }
        : {
            enumeration:
              abbreviation && enumeration !== '§ 0' ? enumeration : undefined,
          }),
    })
  }
  return ref.value
}

/** Absolute variant of `getRefPath` for links that leave the app (stamped PDFs). */
export function getRefUrl(ref: ParserLink, clauseSlug?: string): string {
  const path = getRefPath(ref, clauseSlug)
  return path.startsWith('http') ? path : `${PRODUCTION_DOMAIN}${path}`
}
